import { ShieldAlert } from "lucide-react"
import { comoFechaHora } from "@/lib/fechas"
import { BadgeEstatus, type Estatus } from "./BadgeEstatus"

/**
 * Línea de tiempo del expediente, desde `expediente_transiciones`.
 *
 * La primera fila es la de apertura, con origen y destino iguales. Se muestra
 * como «Apertura» en vez de «POR_LLEGAR → POR_LLEGAR», que se lee como un error.
 *
 * Un avance forzado se marca **explícitamente y con su motivo a la vista**. Es el
 * punto del sistema donde alguien se saltó la compuerta documental; esconderlo en
 * la auditoría lo dejaría fuera del alcance de quien revisa el expediente, que es
 * justo quien necesita verlo.
 */

export interface Transicion {
  id: number
  estatus_desde: Estatus
  estatus_hasta: Estatus
  actor_id: number
  override: boolean
  motivo: string | null
  creado_en: string
}

export function LineaDeTiempo({ transiciones }: { transiciones: Transicion[] }) {
  if (transiciones.length === 0) {
    return (
      // Con recuadro, como todos los vacíos del sistema: una frase suelta bajo un
      // encabezado se lee como que la sección no cargó, no como que está vacía.
      <p className="superficie py-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Sin movimientos registrados
      </p>
    )
  }

  return (
    <ol className="space-y-3">
      {transiciones.map((t) => {
        const esApertura = t.estatus_desde === t.estatus_hasta
        return (
          <li key={t.id} className="superficie p-3">
            <div className="flex flex-wrap items-center gap-2">
              {esApertura ? (
                <span className="text-sm font-medium">Apertura</span>
              ) : (
                <>
                  <BadgeEstatus estatus={t.estatus_desde} />
                  <span aria-hidden="true" style={{ color: "var(--fg-muted)" }}>
                    →
                  </span>
                </>
              )}
              <BadgeEstatus estatus={t.estatus_hasta} />
              <span className="tabular ml-auto text-xs" style={{ color: "var(--fg-muted)" }}>
                {comoFechaHora(t.creado_en)}
              </span>
            </div>

            {t.override && (
              <p
                className="mt-2 flex items-start gap-2 text-xs"
                style={{ color: "var(--warning)" }}
              >
                <ShieldAlert size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  <strong>Avance forzado.</strong> {t.motivo}
                </span>
              </p>
            )}
          </li>
        )
      })}
    </ol>
  )
}
